<template>
  <div class="w-64 px-4 py-3">
    <UiComboboxVee
      name="libraryVisibilityFilter"
      :label="$t('term.status')"
      :options="options"
      :modelValue="visibilityFilter"
      @update:model-value="onSelect" />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { TLibraryVisibilityFilter } from '@/globals/typescript/utils/libraryVisibilityFilter'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useTextListStore } from '@/stores/textListStore'

type TLibraryVisibilityFilterList =
  | 'report-descriptions'
  | 'report-description-items'
  | 'labels-results'
  | 'labels-units'

/**
 * The shared visibility filter for library-style lists with all three
 * lifecycle states — Active / Active and inactive / Archived / All, default
 * Active. Replaces the exclusive two-option active/inactive filter
 * (`UiRadioTabs`) for lists that also have an archived state. See
 * table-conventions.md.
 *
 * One component driving several stores via the `list` prop, mirroring
 * CustomerFilterMenu's `tab` prop.
 */
export default {
  name: 'LibraryVisibilityFilterMenu',

  emits: ['close'],

  props: {
    list: {
      type: String as PropType<TLibraryVisibilityFilterList>,
      required: true,
    },
  },

  computed: {
    options(): { value: TLibraryVisibilityFilter; text: string }[] {
      return [
        { value: 'active', text: this.$t('state.active') },
        { value: 'active-inactive', text: this.$t('W: Aktive og inaktive') },
        { value: 'archived', text: this.$t('state.archived') },
        { value: 'all', text: this.$t('term.all') },
      ]
    },

    visibilityFilter(): TLibraryVisibilityFilter {
      if (this.list === 'report-descriptions') {
        return useReportDescriptionStore().listStateFilter
      }
      if (this.list === 'report-description-items') {
        return useReportDescriptionItemStore().listStateFilter
      }
      if (this.list === 'labels-results') {
        return useTextListStore().resultsVisibilityFilter
      }
      return useTextListStore().unitsVisibilityFilter
    },
  },

  methods: {
    onSelect(value: TLibraryVisibilityFilter): void {
      if (this.list === 'report-descriptions') {
        useReportDescriptionStore().listStateFilter = value
        return
      }
      if (this.list === 'report-description-items') {
        useReportDescriptionItemStore().listStateFilter = value
        return
      }
      if (this.list === 'labels-results') {
        useTextListStore().resultsVisibilityFilter = value
        return
      }
      useTextListStore().unitsVisibilityFilter = value
    },
  },
}
</script>
